import { DataFieldType } from '~/enums/DataFieldType';
import { DateFormatType } from '~/enums/DateFormatType';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { WorkflowTicketRoleType } from '~/enums/WorkflowTicketRoleType';
import { intlService } from '~/i18n/IntlService';

export const customSettingsKeys = {
  [WorkflowFieldType.Address]: {
    DOCUMENT_PLACEMENT: 'document_placement',
  },
  [WorkflowFieldType.Checkbox]: {
    ENABLE_OTHER_OPTION: 'enable_other_option',
    EXCLUDE_OTHER_OPTION_IN_DATA_MAPPING:
      'exclude_other_option_in_data_mapping',
  },
  [WorkflowFieldType.Date]: {
    FORMAT: 'format',
  },
  [WorkflowFieldType.Department]: {
    RULE: 'rule',
    DEPARTMENTS: 'departments',
    ALLOW_MULTIPLE: 'allow_multiple',
  },
  [WorkflowFieldType.Email]: {
    EMAIL_TYPE: 'email_type',
  },
  [WorkflowFieldType.File]: {
    ALLOWED_FILE_TYPE_RULE: 'allowed_file_type_rule',
    ALLOWED_FILE_TYPES: 'allowed_file_types',
    FILE_LIMIT: 'file_limit',
    MIN_FILE_COUNT: 'min_file_count',
    IS_DOCUMENT_PART: 'is_document_part',
  },
  [WorkflowFieldType.MonetaryValue]: {
    CURRENCY_RULE: 'currency_rule',
    CURRENCY_LIST: 'currency_list',
    INCLUDE_SYMBOL_IN_DOCUMENT: 'include_symbol_in_document',
  },
  [WorkflowFieldType.MultiSelect]: {
    ENABLE_CREATE_OPTIONS: 'enable_create_options',
  },
  [WorkflowFieldType.Number]: {
    ALLOW_DECIMALS: 'allow_decimals',
  },
  [WorkflowFieldType.Percentage]: {
    ALLOW_DECIMALS: 'allow_decimals',
    INCLUDE_SYMBOL_IN_DOCUMENT: 'include_symbol_in_document',
    MAX: 'max',
    MIN: 'min',
    OPERATOR: 'operator',
  },
  [WorkflowFieldType.Radio]: {
    ENABLE_OTHER_OPTION: 'enable_other_option',
    EXCLUDE_OTHER_OPTION_IN_DATA_MAPPING:
      'exclude_other_option_in_data_mapping',
  },
  [WorkflowFieldType.SingleSelect]: {
    ENABLE_CREATE_OPTIONS: 'enable_create_options',
  },
  [WorkflowFieldType.User]: {
    RULE: 'rule',
    USERS: 'users',
    ALLOW_MULTIPLE: 'allow_multiple',
  },
};

export const fieldTypesToDataFieldTypes = {
  [WorkflowFieldType.Address]: [],
  [WorkflowFieldType.Boolean]: [],
  [WorkflowFieldType.Checkbox]: [],
  [WorkflowFieldType.Date]: [DataFieldType.DATE],
  [WorkflowFieldType.Department]: [
    DataFieldType.STRING,
    DataFieldType.ARRAY_SINGLE,
    DataFieldType.ARRAY_MULTIPLE,
  ],
  [WorkflowFieldType.Email]: [DataFieldType.STRING],
  [WorkflowFieldType.File]: [DataFieldType.ATTACHMENT],
  [WorkflowFieldType.Initials]: [],
  [WorkflowFieldType.LongResponse]: [DataFieldType.TEXT_AREA],
  [WorkflowFieldType.MonetaryValue]: [],
  [WorkflowFieldType.MultiSelect]: [
    DataFieldType.BOOLEAN,
    DataFieldType.ARRAY_SINGLE,
    DataFieldType.ARRAY_MULTIPLE,
  ],
  [WorkflowFieldType.Number]: [DataFieldType.NUMBER],
  [WorkflowFieldType.Percentage]: [DataFieldType.NUMBER],
  [WorkflowFieldType.Radio]: [
    DataFieldType.BOOLEAN,
    DataFieldType.ARRAY_SINGLE,
    DataFieldType.ARRAY_MULTIPLE,
  ],
  [WorkflowFieldType.ShortResponse]: [
    DataFieldType.STRING,
    DataFieldType.ARRAY_MULTIPLE,
  ],
  [WorkflowFieldType.Signature]: [],
  [WorkflowFieldType.SingleSelect]: [
    DataFieldType.BOOLEAN,
    DataFieldType.ARRAY_SINGLE,
    DataFieldType.ARRAY_MULTIPLE,
  ],
  [WorkflowFieldType.TimePeriod]: [],
  [WorkflowFieldType.User]: [],
};

export const valueKeys = {
  [WorkflowFieldType.Address]: {
    COUNTRY: 'country',
    STREET_1: 'street_1',
    STREET_2: 'street_2',
    CITY: 'city',
    STATE: 'state',
    ZIP_CODE: 'zip_code',
  },
  [WorkflowFieldType.Boolean]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.Checkbox]: {
    VALUE: 'value',
    OTHER_VALUE: 'other_value',
  },
  [WorkflowFieldType.Date]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.Department]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.Email]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.File]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.LongResponse]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.MonetaryValue]: {
    AMOUNT: 'amount',
    CURRENCY: 'currency',
  },
  [WorkflowFieldType.MultiSelect]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.Number]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.Percentage]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.Radio]: {
    VALUE: 'value',
    OTHER_VALUE: 'other_value',
  },
  [WorkflowFieldType.ShortResponse]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.SingleSelect]: {
    VALUE: 'value',
  },
  [WorkflowFieldType.TimePeriod]: {
    PERIOD: 'period',
    AMOUNT: 'amount',
  },
  [WorkflowFieldType.User]: {
    VALUE: 'value',
  },
};

export const customSettingsOptionValues = {
  [WorkflowFieldType.Address]: {
    DOCUMENT_PLACEMENT: {
      MULTIPLE_LINES: 'multiple_lines',
      SINGLE_LINE: 'single_line',
    },
  },
  [WorkflowFieldType.Date]: {
    FORMAT: DateFormatType,
  },
  [WorkflowFieldType.Department]: {
    RULE: {
      ALL: 'all',
      ALL_EXCEPT: 'all_except',
      SPECIFIC: 'specific',
    },
  },
  [WorkflowFieldType.Email]: {
    EMAIL_TYPE: {
      // ANOTHER_FIELD: 'another_field', // to be supported in the future
      MANUAL: 'manual',
      NONE: 'none',
      SUBMITTER: 'submitter',
    },
  },
  [WorkflowFieldType.File]: {
    FILE_LIMIT: {
      SINGLE: 'single',
      MULTIPLE: 'multiple',
      UNLIMITED: 'unlimited',
    },
    FILE_TYPE_RULE: {
      ANY: 'any',
      ANY_EXCEPT: 'any_except',
      SPECIFIC: 'specific',
    },
  },
  [WorkflowFieldType.MonetaryValue]: {
    CURRENCY_RULE: {
      ANY: 'any',
      ANY_EXCEPT: 'any_except',
      SPECIFIC: 'specific',
    },
  },
  [WorkflowFieldType.Percentage]: {
    OPERATOR: {
      BETWEEN: 'between',
      GREATER: 'greater',
      GREATER_OR_EQUAL: 'greater_or_equal',
      LESS: 'less',
      LESS_OR_EQUAL: 'less_or_equal',
    },
  },
  [WorkflowFieldType.User]: {
    RULE: {
      ALL: 'all',
      ALL_EXCEPT: 'all_except',
      SPECIFIC: 'specific',
    },
  },
};

export const getTimePeriodFieldOptions = () => {
  const intl = intlService.getIntl();

  const timePeriods = [
    {
      value: 'second',
      label: intl.formatMessage(
        {
          id: 'constants.Workflow.timePeriodSeconds',
          defaultMessage: '{count, plural, one {Second} other {Seconds}}',
          description: 'Label for seconds time period with pluralization',
        },
        { count: 2 },
      ),
    },
    {
      value: 'minute',
      label: intl.formatMessage(
        {
          id: 'constants.Workflow.timePeriodMinutes',
          defaultMessage: '{count, plural, one {Minute} other {Minutes}}',
          description: 'Label for minutes time period with pluralization',
        },
        { count: 2 },
      ),
    },
    {
      value: 'hour',
      label: intl.formatMessage(
        {
          id: 'constants.Workflow.timePeriodHours',
          defaultMessage: '{count, plural, one {Hour} other {Hours}}',
          description: 'Label for hours time period with pluralization',
        },
        { count: 2 },
      ),
    },
    {
      value: 'day',
      label: intl.formatMessage(
        {
          id: 'constants.Workflow.timePeriodDays',
          defaultMessage: '{count, plural, one {Day} other {Days}}',
          description: 'Label for days time period with pluralization',
        },
        { count: 2 },
      ),
    },
    {
      value: 'week',
      label: intl.formatMessage(
        {
          id: 'constants.Workflow.timePeriodWeeks',
          defaultMessage: '{count, plural, one {Week} other {Weeks}}',
          description: 'Label for weeks time period with pluralization',
        },
        { count: 2 },
      ),
    },
    {
      value: 'month',
      label: intl.formatMessage(
        {
          id: 'constants.Workflow.timePeriodMonths',
          defaultMessage: '{count, plural, one {Month} other {Months}}',
          description: 'Label for months time period with pluralization',
        },
        { count: 2 },
      ),
    },
    {
      value: 'year',
      label: intl.formatMessage(
        {
          id: 'constants.Workflow.timePeriodYears',
          defaultMessage: '{count, plural, one {Year} other {Years}}',
          description: 'Label for years time period with pluralization',
        },
        { count: 2 },
      ),
    },
  ];

  return timePeriods;
};

export const getTimePeriodFieldDefaultOptions = () => {
  const allOptions = getTimePeriodFieldOptions();
  return allOptions.filter((opt) =>
    ['hour', 'day', 'week', 'month', 'year'].includes(opt.value),
  );
};

export const getWorkflowConstants = (intl) => {
  const fieldTypeLabels = {
    [WorkflowFieldType.Address]: intl.formatMessage({
      id: 'constants.Workflow.addressFieldType',
      defaultMessage: 'Address',
      description: 'Label for address field type',
    }),
    [WorkflowFieldType.Boolean]: intl.formatMessage({
      id: 'constants.Workflow.booleanFieldType',
      defaultMessage: 'Boolean',
      description: 'Label for boolean field type',
    }),
    [WorkflowFieldType.Checkbox]: intl.formatMessage({
      id: 'constants.Workflow.checkboxFieldType',
      defaultMessage: 'Checkbox',
      description: 'Label for checkbox field type',
    }),
    [WorkflowFieldType.Date]: intl.formatMessage({
      id: 'constants.Workflow.dateFieldType',
      defaultMessage: 'Date',
      description: 'Label for date field type',
    }),
    [WorkflowFieldType.DateSigned]: intl.formatMessage({
      id: 'constants.Workflow.dateSignedFieldType',
      defaultMessage: 'Date Signed',
      description: 'Label for date signed field type',
    }),
    [WorkflowFieldType.Department]: intl.formatMessage({
      id: 'constants.Workflow.departmentFieldType',
      defaultMessage: 'Department',
      description: 'Label for department field type',
    }),
    [WorkflowFieldType.Email]: intl.formatMessage({
      id: 'constants.Workflow.emailFieldType',
      defaultMessage: 'Email',
      description: 'Label for email field type',
    }),
    [WorkflowFieldType.File]: intl.formatMessage({
      id: 'constants.Workflow.fileFieldType',
      defaultMessage: 'Attachments',
      description: 'Label for file field type',
    }),
    [WorkflowFieldType.Initials]: intl.formatMessage({
      id: 'constants.Workflow.initialsFieldType',
      defaultMessage: 'Initials',
      description: 'Label for initials field type',
    }),
    [WorkflowFieldType.LongResponse]: intl.formatMessage({
      id: 'constants.Workflow.longResponseFieldType',
      defaultMessage: 'Text (Paragraph)',
      description: 'Label for long response field type',
    }),
    [WorkflowFieldType.MonetaryValue]: intl.formatMessage({
      id: 'constants.Workflow.monetaryValueFieldType',
      defaultMessage: 'Monetary Value',
      description: 'Label for monetary value field type',
    }),
    [WorkflowFieldType.MultiSelect]: intl.formatMessage({
      id: 'constants.Workflow.multiSelectFieldType',
      defaultMessage: 'Dropdown (Choose multiple)',
      description: 'Label for multi select field type',
    }),
    [WorkflowFieldType.Number]: intl.formatMessage({
      id: 'constants.Workflow.numberFieldType',
      defaultMessage: 'Number',
      description: 'Label for number field type',
    }),
    [WorkflowFieldType.Percentage]: intl.formatMessage({
      id: 'constants.Workflow.percentageFieldType',
      defaultMessage: 'Percentage',
      description: 'Label for percentage field type',
    }),
    [WorkflowFieldType.Radio]: intl.formatMessage({
      id: 'constants.Workflow.radioFieldType',
      defaultMessage: 'Radio',
      description: 'Label for radio field type',
    }),
    [WorkflowFieldType.Reference]: intl.formatMessage({
      id: 'constants.Workflow.referenceFieldType',
      defaultMessage: 'Reference',
      description: 'Label for reference field type',
    }),
    [WorkflowFieldType.ShortResponse]: intl.formatMessage({
      id: 'constants.Workflow.shortResponseFieldType',
      defaultMessage: 'Text (Single line)',
      description: 'Label for short response field type',
    }),
    [WorkflowFieldType.Signature]: intl.formatMessage({
      id: 'constants.Workflow.signatureFieldType',
      defaultMessage: 'Signature',
      description: 'Label for signature field type',
    }),
    [WorkflowFieldType.SingleSelect]: intl.formatMessage({
      id: 'constants.Workflow.singleSelectFieldType',
      defaultMessage: 'Dropdown (Choose one)',
      description: 'Label for single select field type',
    }),
    [WorkflowFieldType.TimePeriod]: intl.formatMessage({
      id: 'constants.Workflow.timePeriodFieldType',
      defaultMessage: 'Time Period',
      description: 'Label for time period field type',
    }),
    [WorkflowFieldType.User]: intl.formatMessage({
      id: 'constants.Workflow.userFieldType',
      defaultMessage: 'User',
      description: 'Label for user field type',
    }),
  };

  const contractFieldDefinitions = [
    {
      key: 'governingLaw',
      entity: 'contract',
      fieldType: WorkflowFieldType.SingleSelect,
      label: intl.formatMessage({
        id: 'constants.Workflow.governingLawLabel',
        defaultMessage: 'Governing Law',
        description: 'Label for governing law field',
      }),
    },
    {
      key: 'agreementDate',
      entity: 'contract',
      fieldType: WorkflowFieldType.Date,
      label: intl.formatMessage({
        id: 'constants.Workflow.agreementDateLabel',
        defaultMessage: 'Agreement Date',
        description: 'Label for agreement date field',
      }),
    },
    {
      key: 'masterAgreementDate',
      entity: 'contract',
      fieldType: WorkflowFieldType.Date,
      label: intl.formatMessage({
        id: 'constants.Workflow.masterAgreementDateLabel',
        defaultMessage: 'Master Agreement Date',
        description: 'Label for master agreement date field',
      }),
    },
  ];

  const partyFieldDefinitions = [
    {
      key: 'name',
      entity: 'party',
      fieldType: WorkflowFieldType.SingleSelect,
      label: intl.formatMessage({
        id: 'constants.Workflow.partyNameLabel',
        defaultMessage: 'Name',
        description: 'Label for party name field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
    {
      key: 'address',
      entity: 'party',
      fieldType: WorkflowFieldType.Address,
      label: intl.formatMessage({
        id: 'constants.Workflow.partyAddressLabel',
        defaultMessage: 'Address',
        description: 'Label for party address field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
    {
      key: 'phone',
      entity: 'party',
      fieldType: WorkflowFieldType.ShortResponse,
      label: intl.formatMessage({
        id: 'constants.Workflow.partyPhoneLabel',
        defaultMessage: 'Contact Phone',
        description: 'Label for party contact phone field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
    {
      key: 'contactName',
      entity: 'party',
      fieldType: WorkflowFieldType.ShortResponse,
      label: intl.formatMessage({
        id: 'constants.Workflow.partyContactNameLabel',
        defaultMessage: 'Contact Name',
        description: 'Label for party contact name field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
    {
      key: 'text',
      entity: 'party',
      fieldType: WorkflowFieldType.LongResponse,
      label: intl.formatMessage({
        id: 'constants.Workflow.partyTextLabel',
        defaultMessage: 'Text',
        description: 'Label for party text field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
  ];

  const signerFieldDefinitions = [
    {
      key: 'email',
      entity: 'signer',
      fieldType: WorkflowFieldType.Email,
      isRequired: true,
      label: intl.formatMessage({
        id: 'constants.Workflow.signerEmailLabel',
        defaultMessage: 'Email',
        description: 'Label for signer email field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
    {
      key: 'initials',
      entity: 'signer',
      fieldType: WorkflowFieldType.Initials,
      label: intl.formatMessage({
        id: 'constants.Workflow.signerInitialsLabel',
        defaultMessage: 'Signer Initials',
        description: 'Label for signer initials field',
      }),
      isEsignatureTag: true,
      isEsignatureTagOptional: false,
    },
    {
      key: 'name',
      entity: 'signer',
      fieldType: WorkflowFieldType.ShortResponse,
      isRequired: true,
      label: intl.formatMessage({
        id: 'constants.Workflow.signerNameLabel',
        defaultMessage: 'Name',
        description: 'Label for signer name field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
    {
      key: 'signature',
      entity: 'signer',
      fieldType: WorkflowFieldType.Signature,
      isRequired: true,
      label: intl.formatMessage({
        id: 'constants.Workflow.signerSignatureLabel',
        defaultMessage: 'Signature',
        description: 'Label for signer signature field',
      }),
      isEsignatureTag: true,
      isEsignatureTagOptional: false,
    },
    {
      key: 'signDate',
      entity: 'signer',
      fieldType: WorkflowFieldType.DateSigned,
      label: intl.formatMessage({
        id: 'constants.Workflow.signerSignDateLabel',
        defaultMessage: 'Date Signed',
        description: 'Label for signer sign date field',
      }),
      isEsignatureTag: true,
      isEsignatureTagOptional: false,
    },
    {
      key: 'title',
      entity: 'signer',
      fieldType: WorkflowFieldType.ShortResponse,
      label: intl.formatMessage({
        id: 'constants.Workflow.signerTitleLabel',
        defaultMessage: 'Title',
        description: 'Label for signer title field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
    {
      key: 'text',
      entity: 'signer',
      fieldType: WorkflowFieldType.LongResponse,
      label: intl.formatMessage({
        id: 'constants.Workflow.signerTextLabel',
        defaultMessage: 'Text',
        description: 'Label for signer text field',
      }),
      isEsignatureTag: false,
      isEsignatureTagOptional: false,
    },
  ];

  const workflowTicketRoleOptions = [
    {
      value: WorkflowTicketRoleType.Creator,
      label: intl.formatMessage({
        id: 'constants.Workflow.ticketSubmitterLabel',
        defaultMessage: 'Ticket Submitter',
        description: 'Label for ticket submitter role',
      }),
      leftIcon: 'signature',
    },
  ];
  return {
    fieldTypeLabels,
    contractFieldDefinitions,
    partyFieldDefinitions,
    signerFieldDefinitions,
    workflowTicketRoleOptions,
  };
};
